import type { ResponsiveLayout, GridColumn } from "./blocks";

/**
 * Maps a grid column value to responsive Tailwind classes for each breakpoint
 */
const responsiveColSpanClasses: Record<"sm" | "md" | "lg" | "xl", Record<GridColumn, string>> = {
  sm: {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    9: "sm:col-span-9",
    10: "sm:col-span-10",
    11: "sm:col-span-11",
    12: "sm:col-span-12",
  },
  md: {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    7: "md:col-span-7",
    8: "md:col-span-8",
    9: "md:col-span-9",
    10: "md:col-span-10",
    11: "md:col-span-11",
    12: "md:col-span-12",
  },
  lg: {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
    5: "lg:col-span-5",
    6: "lg:col-span-6",
    7: "lg:col-span-7",
    8: "lg:col-span-8",
    9: "lg:col-span-9",
    10: "lg:col-span-10",
    11: "lg:col-span-11",
    12: "lg:col-span-12",
  },
  xl: {
    1: "xl:col-span-1",
    2: "xl:col-span-2",
    3: "xl:col-span-3",
    4: "xl:col-span-4",
    5: "xl:col-span-5",
    6: "xl:col-span-6",
    7: "xl:col-span-7",
    8: "xl:col-span-8",
    9: "xl:col-span-9",
    10: "xl:col-span-10",
    11: "xl:col-span-11",
    12: "xl:col-span-12",
  },
};

/**
 * Converts a ResponsiveLayout object to Tailwind CSS classes for grid column spans.
 * Uses mobile-first approach where xs is the base and larger breakpoints override.
 *
 * @param layout - The responsive layout configuration
 * @returns Space-separated Tailwind classes for column spans
 *
 * @example
 * getLayoutClasses({ xs: 12, md: 6, lg: 4 })
 * // Returns: "col-span-12 md:col-span-6 lg:col-span-4"
 */
export function getLayoutClasses(layout?: ResponsiveLayout): string {
  if (!layout) {
    // Default to full width
    return "col-span-12";
  }

  const classes: string[] = [];

  // Mobile (xs) is always full-width to prevent smushed content on small screens
  classes.push("col-span-12");

  // If xs was set to less than 12, promote it to the sm breakpoint
  // so the configured width takes effect on tablet-sized screens (≥ 640px)
  const xsValue = layout.xs ?? 12;
  if (xsValue < 12 && layout.sm === undefined) {
    classes.push(responsiveColSpanClasses.sm[xsValue]);
  }

  // Responsive breakpoints - only include if defined
  if (layout.sm !== undefined) {
    classes.push(responsiveColSpanClasses.sm[layout.sm]);
  }
  if (layout.md !== undefined) {
    classes.push(responsiveColSpanClasses.md[layout.md]);
  }
  if (layout.lg !== undefined) {
    classes.push(responsiveColSpanClasses.lg[layout.lg]);
  }
  if (layout.xl !== undefined) {
    classes.push(responsiveColSpanClasses.xl[layout.xl]);
  }

  return classes.join(" ");
}

/**
 * CSS classes for the grid container that wraps blocks.
 * Uses a 12-column grid with gap.
 */
export const GRID_CONTAINER_CLASSES = "grid grid-cols-12 gap-4";
